import { useState } from "react";
import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";

// Pre-launch wipe panel. Lives in Admin → Einstellungen and is the one-shot
// button to clear all tipping state before the WM kicks off, after the team
// is done end-to-end testing.
//
// Two safety gates:
//   1. Modal confirmation
//   2. Operator must type the literal word RESET to enable the button
//
// The server also requires `{ confirm: "RESET" }` in the body, so neither
// gate alone can fire the wipe.
export default function WMResetPanel() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [clearLogs, setClearLogs] = useState(true);
  const [busy, setBusy] = useState(false);

  const reset = async () => {
    setBusy(true);
    try {
      const r = await api.admin.resetForWm(clearLogs);
      toast.success(
        `Reset abgeschlossen ✅ — ${r.tipsDeleted} Tipps, ${r.bonusTipsDeleted} Bonus-Tipps, ${r.testImportGamesDeleted} Test/Import-Spiele gelöscht. ${r.profilesReset} Profile zurückgesetzt.`,
        { duration: 8000 },
      );
      setOpen(false);
      setConfirmText("");
      // Hard reload so every cached client view (rankings, profile state,
      // games list) re-fetches from a clean DB.
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Reset fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-destructive/5 border-2 border-destructive/30 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <div>
          <h2 className="font-display font-bold text-lg text-destructive">Gefahrenzone · WM-Reset</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Setzt das gesamte Tippspiel auf <b>sauberen Start vor WM-Anpfiff</b> zurück. Nutze dies erst nach Abschluss aller End-to-End-Tests und <b>vor</b> WM-Beginn.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-xs">
        <div className="bg-card rounded-md border border-border p-3">
          <div className="font-bold mb-1 text-foreground">Wird gelöscht / zurückgesetzt</div>
          <ul className="space-y-0.5 text-muted-foreground list-disc list-inside">
            <li>Alle Tipps</li>
            <li>Alle Bonus-Tipps + Bonus-Truth</li>
            <li>Alle Badges</li>
            <li>Aktivitäts-Feed</li>
            <li>Test-Spiele + Import-Spiele</li>
            <li>Match-Insights (AI-Vorschau)</li>
            <li>Punkte, XP, Level, Streak pro Tipper</li>
            <li>Live-/Endstände echter WM-Spiele (Cron syncht neu)</li>
          </ul>
        </div>
        <div className="bg-card rounded-md border border-border p-3">
          <div className="font-bold mb-1 text-foreground">Bleibt erhalten</div>
          <ul className="space-y-0.5 text-muted-foreground list-disc list-inside">
            <li>Benutzer-Accounts + Profile (Username, Emoji, Avatar)</li>
            <li>Tippgruppen + Mitgliedschaften</li>
            <li>Push-Abos + Benachrichtigungs-Einstellungen</li>
            <li>Admin-Konfiguration (API-Keys, SMTP, etc.)</li>
            <li>Spielplan (echte WM-Spiele)</li>
            <li>Trikot-Einlösungen</li>
          </ul>
        </div>
      </div>

      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground font-bold gap-1.5"
      >
        <Trash2 className="h-4 w-4" /> WM-Reset starten …
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> WM-Reset bestätigen
            </DialogTitle>
            <DialogDescription>
              Diese Aktion ist <b>nicht umkehrbar</b>. Alle Tipps, Bonus-Tipps,
              Punkte und Test-Spiele werden gelöscht. Tippgruppen, Accounts und
              Push-Abos bleiben bestehen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={clearLogs} onCheckedChange={(v) => setClearLogs(!!v)} />
              <span>
                Auch <b>Benachrichtigungs- &amp; AI-Logs</b> löschen (empfohlen für sauberen Start)
              </span>
            </label>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Tippe <code className="bg-muted px-1 py-0.5 rounded">RESET</code> um zu bestätigen
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESET"
                className="mt-1 font-mono"
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Abbrechen</Button>
            <Button
              onClick={reset}
              disabled={busy || confirmText !== "RESET"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 font-bold gap-1.5"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Jetzt zurücksetzen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
